import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  User,
  Settings,
  Heart,
  Camera,
  Crown,
  ChevronRight,
  LogOut,
  Edit2,
  CreditCard,
  Sparkles,
  Check,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useModelsStore } from '@/stores/models-store';
import { useUIStore } from '@/stores/ui-store';
import { useCapturesStore } from '@/stores/captures-store';
import Button from '@/components/ui/Button';
import Avatar from '@/components/ui/Avatar';
import CapturesDrawer from './CapturesDrawer';
import SettingsDrawer from './SettingsDrawer';

type ActiveDrawer = 'captures' | 'settings' | 'subscription' | null;

export default function Profile() {
  const { user, isAuthenticated, logout } = useAuthStore();
  const { favorites } = useModelsStore();
  const { setAuthView, setCurrentTab, showToast } = useUIStore();
  const { captures } = useCapturesStore();

  const [activeDrawer, setActiveDrawer] = useState<ActiveDrawer>(null);

  const handleLogout = async () => {
    await logout();
    showToast('Signed out successfully', 'success');
  };

  const handleFavoritesClick = () => {
    setCurrentTab('library');
    // The library tab will default to favorites
  };

  if (!isAuthenticated) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="h-full flex flex-col items-center justify-center px-6 text-center pt-16"
      >
        {/* Background gradient */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full bg-accent-purple/10 blur-[100px]" />
        </div>

        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="relative"
        >
          <Avatar size="xl" border={true} />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-2xl font-semibold mb-2 mt-6"
        >
          Welcome to VisionAR
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-white/50 mb-8 max-w-xs"
        >
          Sign in to access your profile, favorites, and personalized experience.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-3 w-full max-w-xs"
        >
          <Button fullWidth onClick={() => setAuthView('login')}>
            Sign In
          </Button>
          <Button variant="secondary" fullWidth onClick={() => setAuthView('register')}>
            Create Account
          </Button>
        </motion.div>
      </motion.div>
    );
  }

  const captureCount = captures.length;
  const arSessions = 0; // Would track from a sessions store

  const menuItems = [
    {
      icon: Heart,
      label: 'Favorites',
      value: favorites.length.toString(),
      color: 'text-pink-400',
      bg: 'bg-pink-500/10',
      onClick: handleFavoritesClick,
    },
    {
      icon: Camera,
      label: 'My Captures',
      value: captureCount.toString(),
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      onClick: () => setActiveDrawer('captures'),
    },
    {
      icon: Crown,
      label: 'Subscription',
      value: user?.subscriptionTier || 'Free',
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/10',
      onClick: () => setActiveDrawer('subscription'),
    },
    {
      icon: Settings,
      label: 'Settings',
      value: '',
      color: 'text-white/70',
      bg: 'bg-white/5',
      onClick: () => setActiveDrawer('settings'),
    },
  ];

  return (
    <>
      {/* Drawers */}
      <AnimatePresence>
        {activeDrawer === 'captures' && (
          <CapturesDrawer onClose={() => setActiveDrawer(null)} />
        )}
        {activeDrawer === 'settings' && (
          <SettingsDrawer onClose={() => setActiveDrawer(null)} />
        )}
        {activeDrawer === 'subscription' && (
          <SubscriptionDrawer onClose={() => setActiveDrawer(null)} user={user} />
        )}
      </AnimatePresence>

      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="h-full overflow-y-auto no-scrollbar pb-32"
      >
        {/* Background gradient */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 right-0 w-[300px] h-[300px] rounded-full bg-accent-blue/10 blur-[80px]" />
          <div className="absolute top-40 -left-20 w-[250px] h-[250px] rounded-full bg-accent-purple/10 blur-[80px]" />
        </div>

        {/* Safe Area Spacer */}
        <div className="safe-area-top" />

        {/* Header */}
        <div className="relative px-6 pt-4 pb-8">
          <div className="flex items-center gap-4">
            {/* Avatar with edit button */}
            <div className="relative">
              <Avatar
                src={user?.avatar}
                alt={user?.name || 'Profile'}
                size="xl"
                border={true}
              />
              <button
                onClick={() => showToast('Profile photo editing coming soon', 'info')}
                className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-accent-blue flex items-center justify-center shadow-lg shadow-accent-blue/30 hover:scale-105 transition-transform"
              >
                <Edit2 size={14} />
              </button>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold truncate">{user?.name}</h1>
              <p className="text-white/50 text-sm truncate">{user?.email}</p>
              <button
                onClick={() => setActiveDrawer('subscription')}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-accent-purple/20 to-accent-blue/20 border border-white/10 hover:border-white/20 transition-colors"
              >
                <Crown size={12} className="text-yellow-400" />
                <span className="text-xs font-semibold text-white/90 capitalize">
                  {user?.subscriptionTier}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="relative px-6 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-3xl p-5"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold">Activity</h2>
              <span className="text-xs text-white/40">This month</span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <button onClick={handleFavoritesClick} className="text-center group">
                <p className="text-3xl font-bold gradient-text group-hover:opacity-80 transition-opacity">
                  {favorites.length}
                </p>
                <p className="text-white/50 text-xs mt-1">Favorites</p>
              </button>
              <div className="text-center border-x border-white/10">
                <p className="text-3xl font-bold gradient-text">{arSessions}</p>
                <p className="text-white/50 text-xs mt-1">AR Sessions</p>
              </div>
              <button onClick={() => setActiveDrawer('captures')} className="text-center group">
                <p className="text-3xl font-bold gradient-text group-hover:opacity-80 transition-opacity">
                  {captureCount}
                </p>
                <p className="text-white/50 text-xs mt-1">Captures</p>
              </button>
            </div>
          </motion.div>
        </div>

        {/* Menu */}
        <div className="relative px-6 space-y-2">
          <p className="text-xs text-white/40 uppercase tracking-wider font-semibold mb-3 ml-1">
            Account
          </p>
          {menuItems.map((item, index) => (
            <motion.button
              key={item.label}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 + index * 0.05 }}
              onClick={item.onClick}
              className="w-full glass rounded-2xl p-4 flex items-center gap-4 touch-feedback hover:bg-white/[0.08] transition-colors"
            >
              <div
                className={`w-10 h-10 rounded-full ${item.bg} flex items-center justify-center flex-shrink-0`}
              >
                <item.icon size={20} className={item.color} />
              </div>
              <span className="flex-1 text-left font-medium">{item.label}</span>
              {item.value && <span className="text-white/50 text-sm">{item.value}</span>}
              <ChevronRight size={18} className="text-white/30" />
            </motion.button>
          ))}

          <div className="pt-4">
            <motion.button
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.35 }}
              onClick={handleLogout}
              className="w-full glass rounded-2xl p-4 flex items-center gap-4 touch-feedback hover:bg-red-500/10 transition-colors group"
            >
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-red-500/20 transition-colors">
                <LogOut size={20} className="text-red-400" />
              </div>
              <span className="flex-1 text-left font-medium text-red-400">Sign Out</span>
            </motion.button>
          </div>
        </div>
      </motion.div>
    </>
  );
}

// Subscription Drawer Component (Mocked)
function SubscriptionDrawer({
  onClose,
  user,
}: {
  onClose: () => void;
  user: { subscriptionTier: string } | null;
}) {
  const { showToast } = useUIStore();
  const currentTier = user?.subscriptionTier || 'free';

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: 'forever',
      features: ['5 favorites', '10 AR sessions/month', 'Basic captures', 'Community models'],
      color: 'from-white/20 to-white/10',
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '$9.99',
      period: '/month',
      features: [
        'Unlimited favorites',
        'Unlimited AR sessions',
        'HD captures',
        'Priority support',
        'Exclusive models',
      ],
      color: 'from-accent-blue/30 to-accent-purple/30',
      popular: true,
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '$19.99',
      period: '/month',
      features: [
        'Everything in Pro',
        '4K captures',
        'Custom model uploads',
        'API access',
        'White-label exports',
      ],
      color: 'from-yellow-500/30 to-orange-500/30',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100]"
    >
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Drawer */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="absolute bottom-0 left-0 right-0 max-h-[90vh] bg-background rounded-t-[32px] overflow-hidden flex flex-col"
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="px-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-500/15 flex items-center justify-center">
              <Crown size={20} className="text-yellow-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Subscription</h2>
              <p className="text-sm text-white/50">Choose your plan</p>
            </div>
          </div>
        </div>

        {/* Plans */}
        <div className="flex-1 overflow-y-auto no-scrollbar p-6 space-y-4">
          {plans.map((plan) => {
            const isCurrentPlan = plan.id === currentTier;
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`relative rounded-3xl p-5 border transition-all cursor-pointer ${
                  isCurrentPlan
                    ? 'border-accent-blue/50 bg-accent-blue/5'
                    : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
                }`}
                onClick={() => {
                  if (!isCurrentPlan) {
                    showToast('Payment system coming soon', 'info');
                  }
                }}
              >
                {/* Popular badge */}
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-accent-blue to-accent-purple text-xs font-bold">
                    Most Popular
                  </div>
                )}

                {/* Current plan badge */}
                {isCurrentPlan && (
                  <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full bg-accent-blue/20 text-xs font-semibold text-accent-blue flex items-center gap-1">
                    <Check size={12} />
                    Current
                  </div>
                )}

                {/* Plan header */}
                <div className="flex items-end gap-2 mb-4">
                  <Sparkles
                    size={20}
                    className={
                      plan.id === 'premium'
                        ? 'text-yellow-400'
                        : plan.id === 'pro'
                        ? 'text-accent-blue'
                        : 'text-white/50'
                    }
                  />
                  <h3 className="text-lg font-bold">{plan.name}</h3>
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  <span className="text-white/50 text-sm">{plan.period}</span>
                </div>

                {/* Features */}
                <ul className="space-y-2">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-white/70">
                      <Check size={14} className="text-green-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {!isCurrentPlan && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      showToast('Payment system coming soon', 'info');
                    }}
                    className={`w-full mt-4 py-3 rounded-xl font-semibold transition-all ${
                      plan.popular
                        ? 'bg-gradient-to-r from-accent-blue to-accent-purple text-white'
                        : 'bg-white/10 hover:bg-white/15'
                    }`}
                  >
                    {plan.id === 'free' ? 'Downgrade' : 'Upgrade'}
                  </button>
                )}
              </motion.div>
            );
          })}

          {/* Payment info */}
          <div className="text-center py-4 text-white/40 text-xs">
            <p className="flex items-center justify-center gap-2">
              <CreditCard size={14} />
              Secure payment processing
            </p>
            <p className="mt-1">Cancel anytime. No hidden fees.</p>
          </div>
        </div>

        {/* Safe Area Bottom */}
        <div className="safe-area-bottom" />
      </motion.div>
    </motion.div>
  );
}
